import {getCollections} from '@apps-common/geocorn-sdk/api/collections.js';
import {AddCircle as AddCircleIcon} from '@mui/icons-material';
import {
  Autocomplete,
  CircularProgress,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import {matchSorter} from 'match-sorter';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';

const MAX_NAME_LENGTH = 255;

const COLLECTION_NAME_ERRORS = {
  TOO_LONG: `Collection name cannot exceed ${MAX_NAME_LENGTH} characters.`,
  TOO_SHORT: 'Collection name is too short.',
};

const CreateNewText = props => (
  <Typography sx={{display: 'flex', alignItems: 'center'}}>
    <AddCircleIcon color="primary" sx={{marginRight: 1}} />
    Create &quot;{props.text}&quot;
  </Typography>
);

CreateNewText.propTypes = {
  text: PropTypes.string.isRequired,
};

const CollectionsAutocomplete = ({onChange, ...props}) => {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState(null);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    let active = true;

    if (!open) {
      return undefined;
    }

    (async () => {
      if (active) {
        setLoadingOptions(true);

        const {collections} = await getCollections({
          params: {
            limit: 250,
            can_write: true,
          },
        });

        setOptions([...collections]);
        setLoadingOptions(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const handleOnChange = (_, value) => {
    const newCollection =
      value && (typeof value === 'string' ? value : value.inputValue);

    const newValue = newCollection
      ? {
          id: 'new',
          title: newCollection,
        }
      : value;

    const errorMessage =
      newValue?.title.length === 1
        ? COLLECTION_NAME_ERRORS.TOO_SHORT
        : newValue?.title.length > MAX_NAME_LENGTH
          ? COLLECTION_NAME_ERRORS.TOO_LONG
          : null;

    setValue(newValue);
    setInputError(errorMessage);

    if (!errorMessage && onChange) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    setValue(props.value || null);
  }, [props.value]);

  const onInputChange = (_, newInputValue) => {
    setInputValue(newInputValue);
  };

  return (
    <Autocomplete
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      fullWidth
      freeSolo
      forcePopupIcon
      size="small"
      margin="normal"
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      value={value}
      onChange={handleOnChange}
      inputValue={inputValue}
      onInputChange={onInputChange}
      filterOptions={(options, {inputValue}) => {
        const filtered = matchSorter(options, inputValue, {
          keys: ['title', 'id'],
        });
        // Suggest the creation of a new value
        if (
          inputValue !== '' &&
          inputValue.length > 1 &&
          inputValue.length < MAX_NAME_LENGTH + 1
        ) {
          filtered.push({
            inputValue: inputValue,
            title: <CreateNewText text={inputValue} />,
          });
        }
        // add default suggestion
        if (!inputValue && options.length === 0 && !value && !loadingOptions) {
          filtered.push({
            inputValue: 'New Collection',
            title: <CreateNewText text="New Collection" />,
          });
        }
        return filtered;
      }}
      options={options}
      loading={loadingOptions}
      renderOption={(props, option) => {
        return (
          <ListItem
            {...props}
            key={option.id || option.inputValue}
            aria-label={option.inputValue || option.title}
          >
            <ListItemText disableTypography>
              <Typography component="div" noWrap>
                {option.title}
              </Typography>
              <Typography
                component="div"
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                }}
              >
                {option.id}
              </Typography>
            </ListItemText>
          </ListItem>
        );
      }}
      getOptionLabel={option => {
        // Value selected with enter, right from the input
        if (typeof option === 'string') {
          return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        // Regular option
        return option.title;
      }}
      renderInput={params => (
        <TextField
          {...params}
          error={!!inputError}
          label="Collection"
          helperText={
            inputError
              ? inputError
              : 'You may create a new collection by typing in the textfield'
          }
          elevation={0}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loadingOptions ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
    />
  );
};

CollectionsAutocomplete.propTypes = {
  onChange: PropTypes.func,
  suggestion: PropTypes.string,
  value: PropTypes.object,
};

export default CollectionsAutocomplete;
